import { useEffect, useState } from 'react';
import { useMooChatContext, useMooConfig } from '../provider';
import { useShellAgents } from '../provider/hooks';
import type { PersistedContextEntry } from '../types/adapters';
import type { Context } from '../integrations/context-types';

/** Rehydrates the agent selection + persisted user contexts from the
 *  host's `StorageAdapter`, then writes future selection changes back.
 *
 *  Two phases:
 *
 *  1. **Hydrate (once, gated on `agents.status === 'ready'`):**
 *     - Read `getSelectedAgentId()`; if non-null and the agent exists in
 *       the catalog, dispatch `AGENT_SELECTED` — but only when no agent
 *       is selected yet, so the host's auto-select-default effect can't
 *       race us.
 *     - Read `getPersistedContexts()`; if non-empty, dispatch
 *       `CONTEXTS_USER_HYDRATED` with the rehydrated `Context[]`.
 *
 *  2. **Persist (on every relevant state change):**
 *     - Selection writes go through `setSelectedAgentId`.
 *     - Pinned user contexts write back via `setPersistedContexts`.
 *       Unpinned contexts are ephemeral and intentionally skipped.
 *
 *  No-ops when `config.storage` is omitted. Safe to mount inside any
 *  child of `MooChatProvider`. */
export function useStorageHydration(): void {
  const { storage, logger } = useMooConfig();
  const { state, dispatch } = useMooChatContext();
  const agents = useShellAgents();
  const [hasHydrated, setHasHydrated] = useState(false);

  // Phase 1: hydrate once when the agents catalog reaches a terminal
  // state. When no storage adapter is wired, flip the gate immediately
  // so the host's default-agent effect can run unblocked. When the
  // catalog errored, also flip the gate — the host's auto-select will
  // no-op for lack of a defaultAgentId, but downstream effects that
  // wait on hydration mustn't deadlock just because we can't validate
  // the persisted id against an empty catalog.
  useEffect(() => {
    if (hasHydrated) return;
    if (!storage) {
      if (!state.storageHydrated) dispatch({ type: 'STORAGE_HYDRATED' });
      setHasHydrated(true);
      return;
    }
    if (agents.status === 'idle' || agents.status === 'loading') return;

    // Persisted pinned contexts hydrate regardless of agents catalog
    // state — they're attached to the session, not validated against
    // the catalog. Reading these even when the catalog errored means
    // the user's pinned entities survive a transient agents-API
    // failure on session resume.
    const persistedContexts = safeRead(
      () => storage.getPersistedContexts(),
      logger,
      'getPersistedContexts',
    );
    if (persistedContexts && persistedContexts.length > 0) {
      const rehydrated = persistedContexts
        .map(entryToContext)
        .filter((c): c is Context => c !== null);
      if (rehydrated.length > 0) {
        dispatch({ type: 'CONTEXTS_USER_HYDRATED', contexts: rehydrated });
      }
    }

    // The agentId hydrate path *does* need the catalog so we can
    // validate the persisted id against current membership and not
    // dispatch AGENT_SELECTED for an agent that no longer exists.
    if (agents.status === 'ready') {
      const persistedAgentId = safeRead(
        () => storage.getSelectedAgentId(),
        logger,
        'getSelectedAgentId',
      );
      if (
        persistedAgentId &&
        agents.byId[persistedAgentId] &&
        !state.selection.agentId
      ) {
        dispatch({ type: 'AGENT_SELECTED', agentId: persistedAgentId });
      }
    }

    // Terminal state reached (ready or error). Dispatch the gate flip
    // in the same batch as any AGENT_SELECTED above so the host's
    // auto-select effect on the next render sees `storageHydrated` and
    // `selection.agentId` consistently (no race against the default).
    dispatch({ type: 'STORAGE_HYDRATED' });
    setHasHydrated(true);
  }, [
    storage,
    hasHydrated,
    agents.status,
    agents.byId,
    state.selection.agentId,
    state.storageHydrated,
    dispatch,
    logger,
  ]);

  // Phase 2a: persist agent selection changes.
  useEffect(() => {
    if (!storage || !hasHydrated) return;
    safeWrite(
      () => storage.setSelectedAgentId(state.selection.agentId),
      logger,
      'setSelectedAgentId',
    );
  }, [storage, hasHydrated, state.selection.agentId, logger]);

  // Phase 2b: persist pinned user contexts. Unpinned entries are
  // ephemeral (cleared on send) and intentionally excluded from disk —
  // matches the V1 favorites/pinned vs ephemeral split.
  useEffect(() => {
    if (!storage || !hasHydrated) return;
    const pinned = state.contexts.user
      .map((c, idx) => contextToEntry(c, idx))
      .filter((e): e is PersistedContextEntry => e !== null);
    safeWrite(
      () => storage.setPersistedContexts(pinned),
      logger,
      'setPersistedContexts',
    );
  }, [storage, hasHydrated, state.contexts.user, logger]);
}

function safeRead<T>(
  fn: () => T,
  logger: ReturnType<typeof useMooConfig>['logger'],
  op: string,
): T | null {
  try {
    return fn();
  } catch (err) {
    logger.warn('moo.storage.read.failed', { op, error: String(err) });
    return null;
  }
}

function safeWrite(
  fn: () => void,
  logger: ReturnType<typeof useMooConfig>['logger'],
  op: string,
): void {
  try {
    fn();
  } catch (err) {
    logger.warn('moo.storage.write.failed', { op, error: String(err) });
  }
}

function entryToContext(entry: PersistedContextEntry): Context | null {
  if (entry.kind === 'entity' || entry.kind === 'document' || entry.kind === 'selection') {
    const data = entry.data as Record<string, unknown>;
    return { kind: entry.kind, ...data, pinned: true } as Context;
  }
  return null;
}

function contextToEntry(
  context: Context,
  idx: number,
): PersistedContextEntry | null {
  if (!context.pinned) return null;
  if (context.kind === 'screen') return null;
  const { kind, pinned: _pinned, ...rest } = context;
  return {
    id: `${kind}-${idx}`,
    kind,
    data: rest,
    pinned: true,
    origin: 'manual',
    addedAt: 0,
  };
}
